import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';

export const AccountInformation: React.FC<{
  customIbanMessage?: string;
  customBicMessage?: string;
  customReceiverMessage?: string;
  referenceCode: string | null;
  /**
   * Per-invoice flag from the backend (GALAXY-402). When `false` the
   * bank-transfer details must not be displayed (e.g. invoice was paid via
   * credit card or PayPal). When `undefined`/`null` (e.g. before the backend
   * rollout) the previous behaviour is kept and the block is shown if the
   * tenant config allows it.
   */
  showBankConnection?: boolean | null;
}> = ({
  customIbanMessage,
  customBicMessage,
  customReceiverMessage,
  referenceCode,
  showBankConnection,
}) => {
  const {
    order: {
      accountInformation: { bankTransferEnabled, bicAvailable },
      bankInformation,
    },
  } = useConfig();

  if (!bankTransferEnabled || showBankConnection === false) {
    return null;
  }

  const { companyName, bankName, iban, bic } = bankInformation;

  return (
    <div className="border-color-grey text-size-body-sm space-y-1 rounded border p-2">
      <div className="text-size-body-base mb-2 text-center font-bold">
        <FormattedMessage
          key="AccountInformation.headline-text"
          id="AccountInformation.headline"
          defaultMessage="Unsere Bankverbindung"
        />
      </div>
      <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
        <div>
          <FormattedMessage
            key="AccountInformation.receiverLabel-text"
            id="AccountInformation.receiverLabel"
            defaultMessage="Empfänger:"
          />
        </div>
        <div>
          <div>{companyName}</div>
          <div>{customReceiverMessage ?? bankName}</div>
        </div>
        {bicAvailable && (
          <>
            <div>
              <FormattedMessage
                key="AccountInformation.bicLabel-text"
                id="AccountInformation.bicLabel"
                defaultMessage="BIC:"
              />
            </div>
            <div>{customBicMessage ?? bic}</div>
          </>
        )}
        <div>
          <FormattedMessage
            key="AccountInformation.ibanLabel-text"
            id="AccountInformation.ibanLabel"
            defaultMessage="IBAN:"
          />
        </div>
        <div>{customIbanMessage ?? iban}</div>
        <div>
          <FormattedMessage
            key="AccountInformation.referenceCodeLabel-text"
            id="AccountInformation.referenceCodeLabel"
            defaultMessage="Verwendungszweck:"
          />
        </div>
        <div>
          <FormattedMessage
            key="AccountInformation.referenceCode-text"
            id="AccountInformation.referenceCode"
            defaultMessage="[Kundennummer - Name Nachname]"
            values={{
              bankTransferReference: referenceCode,
              strong: (chunks: React.ReactNode) => (
                <strong data-clarity-mask="True">{chunks}</strong>
              ),
              strongWithPersonalData: (chunks: React.ReactNode) => (
                <strong data-clarity-mask="True">{chunks}</strong>
              ),
            }}
          />
        </div>
      </div>
    </div>
  );
};
